import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Form, Row } from 'antd';
import debounce from 'lodash.debounce';
import shallow from 'zustand/shallow';
import produce from 'immer';

import { BaseModalProps } from 'global/Modal/BaseModal';
import Checkbox from 'global/Securities/components/Checkbox';
import useShareTradeTarget from 'stores/sharetrade/useShareTradeTarget';
import { Target } from 'typings/api/shareTradeTarget';
import { TargetShareItem } from 'typings/api/shareTradeShare';
import useShareTradeShare from 'stores/sharetrade/useShareTradeShare';
import useShareTradeModal from 'features/sharetrade/stores/useShareTradeModal';
import { UserItemTarget } from 'features/edit-profile/ShareTrade/UserList';
import useUIConfigStore from 'stores/useUIConfigStore';
import EmptyState from './EmptyState';
import {
  Alert,
  HeaderRow,
  FooterCol,
  ListTitle,
  SearchInput,
  UserList,
  SubmitButton,
  AgreementText,
  AgreementRow,
  Modal,
  ErrorState,
} from './styled';

export interface FormValues {
  targets: TargetShareItem[];
  saveAsAutoShare: boolean;
}

interface ModalShareTradeProps extends BaseModalProps {
  onShareSubmit?(isAddAutoShare: boolean): void;
}

const ModalShareTrade = ({
  title = 'Share Trade',
  width = 430,
  onShareSubmit = () => {},
  ...props
}: ModalShareTradeProps) => {
  const [form] = Form.useForm();

  const [selectedUsers, setSelectedUsers] = useState<Record<string, Target>>(
    {},
  );

  const [isTransactionBannerShown, setUIValue] = useUIConfigStore(
    (s) => [s.getUIValue('shareTrade')?.isTransactionBannerShown, s.setUIValue],
    shallow,
  );

  const [sharedUsers, setSharedUsers] = useShareTradeModal(
    (s) => [s.sharedUsers, s.setSharedUsers],
    shallow,
  );

  const [
    targetData,
    targetError,
    isTargetLoading,
    isEmptyList,
    loadShareTradeTarget,
    resetShareTradeTarget,
  ] = useShareTradeTarget(
    (s) => [
      s.data,
      s.error,
      s.isLoading,
      s.isEmptyList,
      s.loadShareTradeTarget,
      s.resetShareTradeTarget,
    ],
    shallow,
  );

  const [transactionData, isShareLoading, loadShareTradeShare] =
    useShareTradeShare(
      (s) => [s.transactionData, s.isLoading, s.loadShareTradeShare],
      shallow,
    );

  const isEmptySearch = useMemo(
    () =>
      targetData?.targets?.length === 0 && !!form.getFieldValue('userSearch'),
    [targetData?.targets?.length === 0, !!form.getFieldValue('userSearch')],
  );

  const isShowAutoShare = useMemo(
    () => !targetError && !isEmptyList,
    [targetError, isEmptyList],
  );

  const isShowUserList = useMemo(
    () => isShowAutoShare && !isEmptySearch,
    [isShowAutoShare, isEmptySearch],
  );

  const isDisableSubmit = useMemo(
    () => Object.values(selectedUsers).length === 0 || isShareLoading,
    [Object.values(selectedUsers).length, isShareLoading],
  );

  const userListData = useMemo(() => {
    const filteredTargetData = (targetData?.targets || []).reduce(
      (prev, curr) => {
        if (sharedUsers[curr.id]) return prev;
        return [...prev, curr];
      },
      [],
    );

    const taggedSharedUsers: UserItemTarget[] = Object.values(sharedUsers).map(
      (item) => ({ ...item, is_shared: true }),
    );

    return [...taggedSharedUsers, ...filteredTargetData];
  }, [
    JSON.stringify(targetData?.targets),
    JSON.stringify(sharedUsers),
    isTargetLoading,
  ]);

  const handleBannerClose = () => {
    setUIValue('shareTrade', (state) => {
      state.isTransactionBannerShown = true;
    });
  };

  const handleResetSelectedUsers = useCallback(() => {
    setSelectedUsers({});
  }, [setSelectedUsers]);

  const handleSelectedUserChange = (
    isSelected: boolean,
    userTarget: Target,
  ) => {
    setSelectedUsers(
      produce(selectedUsers, (state) => {
        if (!isSelected && state[userTarget.id]) {
          delete state[userTarget.id];
          return;
        }

        state[userTarget.id] = userTarget;
      }),
    );
  };

  const handleLoadUserNextPage = () => {
    if (targetData?.pagination?.next_cursor) {
      loadShareTradeTarget({
        cursor: targetData.pagination.next_cursor,
        keyword: form.getFieldValue('userSearch'),
      });
    }
  };

  const handleSearchChange = debounce(() => {
    loadShareTradeTarget({ keyword: form.getFieldValue('userSearch') }, true);
  }, 300);

  const handleShareClick = async () => {
    await loadShareTradeShare({
      ...transactionData,
      targets: Object.values(selectedUsers).map(
        ({ id, type }) => ({ id, type } as TargetShareItem),
      ),
      save_as_autoshare: form.getFieldValue('saveAsAutoShare'),
    });

    setSharedUsers({
      ...sharedUsers,
      ...selectedUsers,
    });
    onShareSubmit(form.getFieldValue('saveAsAutoShare'));
  };

  const handleInitialLoad = useCallback(() => {
    loadShareTradeTarget();
  }, [loadShareTradeTarget]);

  const handleResetShareTrade = useCallback(() => {
    handleResetSelectedUsers();
    resetShareTradeTarget();
    form.resetFields();
  }, [handleResetSelectedUsers, resetShareTradeTarget]);

  useEffect(() => {
    if (props.visible) handleInitialLoad();
    else handleResetShareTrade();

    return () => {
      handleResetShareTrade();
    };
  }, [props.visible, handleInitialLoad, handleResetShareTrade]);

  return (
    <Modal title={title} width={width} {...props}>
      {targetError && (
        <ErrorState
          iconSrc="/icon/error-document-retry.svg"
          description="Unable to Load Data"
          onRetry={handleInitialLoad}
          loading={isTargetLoading}
        />
      )}

      {isEmptyList && (
        <EmptyState
          textColor="#969696"
          height="276px"
          src="icon/empty-state-outline.svg"
          infoText={
            <>
              You can only share trades with your groups
              <br /> or friends in your chat list
            </>
          }
        />
      )}

      {isShowAutoShare && (
        <Form form={form}>
          <HeaderRow>
            {!isTransactionBannerShown && (
              <Col span={24}>
                <Alert
                  message="You can manage your Auto-Share list from Settings"
                  afterClose={handleBannerClose}
                />
              </Col>
            )}

            <Form.Item name="userSearch" noStyle>
              <SearchInput
                name="userSearch"
                placeholder="Search chat list"
                onChange={handleSearchChange}
              />
            </Form.Item>

            <Col span={24}>
              <ListTitle>Send to</ListTitle>
            </Col>
          </HeaderRow>

          <Row>
            <Col span={24}>
              {isEmptySearch && (
                <EmptyState
                  height="356px"
                  src="icon/empty-state-outline.svg"
                  infoText="No result found. Chat with your friends first."
                />
              )}

              {isShowUserList && (
                <UserList
                  data={userListData}
                  defaultCheckedKey="is_shared"
                  disableSharedKey={['is_autoshare_active', 'is_shared']}
                  onSelectedUserChange={handleSelectedUserChange}
                  onScrollToBottom={handleLoadUserNextPage}
                  disableSharedUsers
                />
              )}
            </Col>
          </Row>

          <FooterCol span={24}>
            <Row align="middle">
              <Col span={24}>
                <AgreementRow align="middle" justify="space-between">
                  <Col>
                    <AgreementText>
                      Auto Share My Subsequent Orders
                    </AgreementText>
                  </Col>
                  <Form.Item
                    name="saveAsAutoShare"
                    initialValue={false}
                    valuePropName="checked"
                    noStyle
                  >
                    <Checkbox />
                  </Form.Item>
                </AgreementRow>
              </Col>

              <Col span={24}>
                <SubmitButton
                  type="primary"
                  onClick={handleShareClick}
                  loading={isShareLoading}
                  disabled={isDisableSubmit}
                  block
                >
                  Share
                </SubmitButton>
              </Col>
            </Row>
          </FooterCol>
        </Form>
      )}
    </Modal>
  );
};

export default ModalShareTrade;
